import React, { useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { api } from "../lib/api";
import { toast } from "sonner";
import { ImagePlus, FolderOpen, Upload, Loader2, X } from "lucide-react";

const norm = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();

// Fuzzy-match a filename to a member by shared name tokens.
function suggestMember(filename, members) {
  const tokens = new Set(norm(filename).split(" ").filter((w) => w.length >= 2));
  let best = null, bestScore = 0;
  members.forEach((m) => {
    const names = [m.first_name, m.last_name, m.nickname, ...(m.full_name || "").split(" ")]
      .map(norm).filter((w) => w.length >= 2);
    let score = 0;
    names.forEach((n) => { if (tokens.has(n)) score += n.length; });
    if (score > bestScore) { bestScore = score; best = m.id; }
  });
  return bestScore >= 3 ? best : "";
}

const yearIn = (filename) => { const m = String(filename).match(/(?:18|19|20)\d{2}/); return m ? m[0] : ""; };

export function BulkPhotoImportDialog({ open, onOpenChange, members, onDone }) {
  const filesRef = useRef(null);
  const folderRef = useRef(null);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);

  const addFiles = (fileList) => {
    const imgs = Array.from(fileList || []).filter((f) => f.type.startsWith("image/"));
    if (!imgs.length) return;
    const next = imgs.map((file, i) => ({
      id: `${Date.now()}-${i}-${file.name}`,
      file,
      url: URL.createObjectURL(file),
      memberId: suggestMember(file.name, members),
      year: yearIn(file.name),
      caption: "",
    }));
    setRows((r) => [...r, ...next]);
  };

  const onDrop = (e) => { e.preventDefault(); addFiles(e.dataTransfer.files); };
  const removeRow = (id) => setRows((r) => r.filter((x) => x.id !== id));
  const setRow = (id, patch) => setRows((r) => r.map((x) => (x.id === id ? { ...x, ...patch } : x)));

  const assignedCount = rows.filter((r) => r.memberId).length;

  const importAll = async () => {
    const toImport = rows.filter((r) => r.memberId);
    if (!toImport.length) return toast.error("Assign at least one photo to a person");
    setBusy(true); setProgress(0);
    let done = 0, failed = 0;
    for (const row of toImport) {
      try {
        const updated = await api.uploadGalleryPhoto(row.memberId, row.file);
        const added = (updated.gallery || []).slice(-1)[0];
        if (added && (row.caption.trim() || row.year.trim())) {
          await api.setPhotoMeta(row.memberId, added, row.caption.trim(), row.year.trim());
        }
        done += 1;
      } catch (e) { failed += 1; }
      setProgress(done + failed);
    }
    setBusy(false);
    toast.success(`Added ${done} photo${done === 1 ? "" : "s"}${failed ? ` · ${failed} skipped (limit or error)` : ""}`);
    onDone?.();
    close(false);
  };

  const close = (o) => { onOpenChange(o); if (!o) { rows.forEach((r) => URL.revokeObjectURL(r.url)); setRows([]); setProgress(0); } };

  const memberName = (id) => members.find((m) => m.id === id)?.full_name || "Choose person…";

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="bulk-photo-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-serif text-2xl text-[#082D4A]"><ImagePlus size={20} /> Import family photos</DialogTitle>
          <DialogDescription>Drop a folder of photos — we'll suggest who each one belongs to from the file name. Review and save.</DialogDescription>
        </DialogHeader>

        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={onDrop}
          data-testid="bulk-dropzone"
          className="flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-[#D8E0E8] bg-[#F4F6F8] px-4 py-8 text-center"
        >
          <Upload size={26} className="mb-2 text-[#D4A017]" />
          <p className="text-sm text-[#475569]">Drag photos or a folder here</p>
          <div className="mt-3 flex gap-2">
            <Button variant="outline" onClick={() => filesRef.current?.click()} data-testid="bulk-choose-files" className="rounded-full border-[#D8E0E8] bg-white text-[#082D4A]"><ImagePlus size={15} className="mr-1.5" /> Choose photos</Button>
            <Button variant="outline" onClick={() => folderRef.current?.click()} data-testid="bulk-choose-folder" className="rounded-full border-[#D8E0E8] bg-white text-[#082D4A]"><FolderOpen size={15} className="mr-1.5" /> Choose folder</Button>
          </div>
          <input ref={filesRef} type="file" accept="image/*" multiple className="hidden" data-testid="bulk-files-input" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
          <input ref={folderRef} type="file" webkitdirectory="" directory="" multiple className="hidden" data-testid="bulk-folder-input" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
        </div>

        {rows.length > 0 && (
          <>
            <div className="mt-3 flex items-center justify-between text-sm text-[#64748B]">
              <span>{rows.length} photo{rows.length === 1 ? "" : "s"} · <span className="font-medium text-[#082D4A]">{assignedCount} matched</span></span>
              <button onClick={() => { rows.forEach((r) => URL.revokeObjectURL(r.url)); setRows([]); }} className="text-[#94A3B8] hover:text-[#082D4A]" data-testid="bulk-clear">Clear all</button>
            </div>
            <div className="mt-2 max-h-[46vh] space-y-2 overflow-y-auto">
              {rows.map((row) => (
                <div key={row.id} data-testid={`bulk-row-${row.id}`} className="flex items-center gap-3 rounded-xl border border-[#D8E0E8] bg-white p-2">
                  <img src={row.url} alt="" className="h-14 w-14 shrink-0 rounded-lg object-cover" />
                  <div className="grid flex-1 grid-cols-1 gap-2 sm:grid-cols-12">
                    <select
                      value={row.memberId}
                      onChange={(e) => setRow(row.id, { memberId: e.target.value })}
                      data-testid={`bulk-member-select-${row.id}`}
                      className={`col-span-5 rounded-md border px-2 py-1.5 text-sm ${row.memberId ? "border-[#082D4A]/40 bg-[#E8EEF3]" : "border-[#D8E0E8]"}`}
                    >
                      <option value="">Choose person…</option>
                      {members.map((m) => <option key={m.id} value={m.id}>{m.full_name}</option>)}
                    </select>
                    <Input value={row.caption} onChange={(e) => setRow(row.id, { caption: e.target.value })} placeholder="Caption" data-testid={`bulk-caption-${row.id}`} className="col-span-5 h-8 text-sm" />
                    <Input value={row.year} onChange={(e) => setRow(row.id, { year: e.target.value })} placeholder="Year" data-testid={`bulk-year-${row.id}`} className="col-span-2 h-8 text-sm" />
                  </div>
                  <button onClick={() => removeRow(row.id)} className="text-[#94A3B8] hover:text-red-500" data-testid={`bulk-remove-${row.id}`}><X size={16} /></button>
                </div>
              ))}
            </div>
            <Button onClick={importAll} disabled={busy || !assignedCount} data-testid="bulk-import-button" className="mt-3 w-full bg-[#082D4A] text-white hover:bg-[#08223A]">
              {busy ? <><Loader2 size={16} className="mr-1.5 animate-spin" /> Importing {progress}/{assignedCount}…</> : <>Import {assignedCount} photo{assignedCount === 1 ? "" : "s"}</>}
            </Button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
